/** Dev-only preview for the loyalty mini-game (see modules/game/DOCS.md).
 *  Route: /dev/game — same convention as /dev/animations. */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameScreen } from '../../modules/game/components/GameScreen/GameScreen';
import { GameTheme } from '../../modules/game/game.constants';
import { useLoyaltyStore } from '../../modules/loyalty/hooks/useLoyaltyStore';
import { useAppStore } from '../../shared/hooks/useAppStore';
import type { VendingMachineTypeId } from '../../shared/types';
import styles from './DevGame.module.css';

const THEMES: GameTheme[] = ['coffee', 'popcorn', 'snack', 'water'];

/** One real machine type per theme, so the preview exercises the same
 *  THEME_BY_MACHINE_TYPE path the success screen uses. */
const TYPE_ID_BY_THEME: Record<GameTheme, VendingMachineTypeId> = {
    coffee: '5',
    popcorn: '16',
    snack: '1',
    water: '11',
};

const DevGame = () => {
    const navigate = useNavigate();
    const [theme, setTheme] = useState<GameTheme>('coffee');
    const [isGameOpen, setGameOpen] = useState(false);

    /** Seeds the stores a real payment would fill, then opens the success
     *  screen — the full banner → game → ending flow. A fresh fake order id
     *  each time, so the one-try-per-order rule doesn't lock the preview. */
    const seedAndOpenSuccess = (): void => {
        const fakeOrderId = String(Date.now() % 1000000);
        useAppStore.setState((s) => ({
            order: { ...s.order, id: fakeOrderId, user_id: 1 },
            vm: {
                ...s.vm,
                name: 'DEV VM',
                type_id: TYPE_ID_BY_THEME[theme],
            },
        }));
        useLoyaltyStore.setState({ loyalty_id: 1 });
        navigate('/dev/success');
    };

    return (
        <div className={styles.page}>
            <h1>Game dev preview</h1>
            <div className={styles.row}>
                {THEMES.map((th) => (
                    <button
                        key={th}
                        type="button"
                        className={th === theme ? styles.themeActive : styles.theme}
                        onClick={() => setTheme(th)}
                    >
                        {th}
                    </button>
                ))}
            </div>
            <div className={styles.row}>
                <button type="button" className={styles.action} onClick={() => setGameOpen(true)}>
                    Open game directly
                </button>
                <button type="button" className={styles.action} onClick={seedAndOpenSuccess}>
                    Seed state → success screen
                </button>
            </div>
            <p className={styles.hint}>
                «Open game directly» не палить спробу і не шле нарахування. «Seed state» проходить
                справжній флоу банера; нарахування впаде тихо, якщо BFF не запущений.
            </p>
            {isGameOpen && (
                <GameScreen
                    theme={theme}
                    record={0}
                    soundEnabled={false}
                    onSoundToggle={() => undefined}
                    onTryStart={() => undefined}
                    onThreshold={() => undefined}
                    onFinish={() => true}
                    onGameEnd={() => undefined}
                    onClose={() => setGameOpen(false)}
                />
            )}
        </div>
    );
};

export { DevGame };
